import { defined } from "@entrilia/shared/utilities/typeHelper";
import { getCaptureTimes, getThumbnailDimensions, isFrameTooDark } from "./videoThumbnailFrame";

const VIDEO_EVENT_TIMEOUT_MS = 30_000;
const THUMBNAIL_JPEG_QUALITY = 0.9;

export type VideoFrameSource = { file: File } | { url: string };

const waitForVideoEvent = (video: HTMLVideoElement, eventName: "loadedmetadata" | "seeked"): Promise<void> =>
  new Promise((resolve, reject) => {
    const handleEvent = () => {
      cleanUp();
      resolve();
    };
    const handleError = () => {
      cleanUp();
      reject(new Error("The video could not be decoded"));
    };
    const timeout = window.setTimeout(() => {
      cleanUp();
      reject(new Error(`Timed out waiting for the video "${eventName}" event`));
    }, VIDEO_EVENT_TIMEOUT_MS);
    const cleanUp = () => {
      window.clearTimeout(timeout);
      video.removeEventListener(eventName, handleEvent);
      video.removeEventListener("error", handleError);
    };

    video.addEventListener(eventName, handleEvent);
    video.addEventListener("error", handleError);
  });

const encodeJpeg = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("The frame could not be encoded"))),
      "image/jpeg",
      THUMBNAIL_JPEG_QUALITY
    );
  });

export const captureVideoFrame = async (source: VideoFrameSource, fileName: string): Promise<File> => {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";

  const objectUrl = "file" in source ? URL.createObjectURL(source.file) : undefined;
  if ("url" in source) {
    video.crossOrigin = "anonymous";
  }

  try {
    const metadataLoaded = waitForVideoEvent(video, "loadedmetadata");
    video.src = "url" in source ? source.url : defined(objectUrl);
    await metadataLoaded;

    const { width, height } = getThumbnailDimensions({ width: video.videoWidth, height: video.videoHeight });
    if (width === 0 || height === 0 || video.duration === 0) {
      throw new Error("The video has no picture");
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("This browser can't process images");
    }

    for (const time of getCaptureTimes(video.duration)) {
      const seeked = waitForVideoEvent(video, "seeked");
      video.currentTime = time;
      await seeked;
      context.drawImage(video, 0, 0, width, height);
      if (!isFrameTooDark(context.getImageData(0, 0, width, height).data)) {
        break;
      }
    }

    return new File([await encodeJpeg(canvas)], fileName, { type: "image/jpeg" });
  } finally {
    video.removeAttribute("src");
    video.load();
    if (objectUrl !== undefined) {
      URL.revokeObjectURL(objectUrl);
    }
  }
};
